import { ReactNode, createContext, useContext, useEffect } from 'react';

import { getCookies, setCookies } from '@helpers/cookies';
import noop from '@helpers/noop';
import {
  actionGetAcademicYearActive,
  actionGetSemesterActive,
} from '@libraries/api/tahunajaran/tahunajaran.function';
import {
  ISemester,
  ITahunAjaran,
} from '@libraries/api/tahunajaran/tahunajaran.types';
import { configEnv } from '@libraries/config';

interface IUseAcademic {
  academicYear: ITahunAjaran;
  semester: ISemester;
  refetch: () => void;
  isLoading?: boolean;
}

const AcademicContext = createContext<IUseAcademic>({
  academicYear: null,
  semester: null,
  refetch: noop,
  isLoading: false,
});

type TAcademicProvider = {
  children: ReactNode;
  token?: string;
};

export const AcademicProvider: React.FC<TAcademicProvider> = ({
  children,
  token,
}) => {
  const {
    data,
    isLoading,
    refetch: refetchAy,
  } = actionGetAcademicYearActive(!!token);
  const {
    data: semester,
    isLoading: loadingSemster,
    refetch: refetchSemester,
  } = actionGetSemesterActive(!!token);
  const maxAge = configEnv.tokenExpired * 60;

  const refetch = () => {
    refetchAy();
    refetchSemester();
  };

  useEffect(() => {
    if (!!semester) {
      const currentSemId = getCookies('semester_id');
      const newSemId = semester?.id?.toString();

      if (currentSemId !== newSemId) {
        setCookies({
          name: 'semester_id',
          value: newSemId,
          maxAge,
        });
      }
    }
  }, [semester]);

  useEffect(() => {
    if (!!data) {
      const currentAyId = getCookies('academic_year_id');
      const newAyId = data?.id?.toString();

      if (currentAyId !== newAyId) {
        setCookies({
          name: 'academic_year_id',
          value: newAyId,
          maxAge,
        });
      }
    }
  }, [data]);

  const value = {
    refetch: refetch,
    academicYear: data,
    semester: semester,
    isLoading: isLoading || loadingSemster,
  };

  return (
    <AcademicContext.Provider value={value}>
      {children}
    </AcademicContext.Provider>
  );
};

export const useAcademic = (): IUseAcademic => useContext(AcademicContext);
